import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  styled,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import ButtonBlue from "../../shared/general/ButtonBlue";
import DrawerCustom from "../../shared/general/Drawer";
import TextFieldTheme from "../../shared/general/TextFieldTheme";

import { getShift, updateShiftGroupHolidayPattern } from "../../../../actions/shift";

const Content = styled("div")({
  width: "500px",
  maxWidth: "500px",
  "& .content-padding": {
    padding: "16px",
  },
  "& .sub-topic": {
    fontWeight: "500",
  },
  "& .value": {
    fontSize: "20px",
    fontWeight: "500",
  },
  "& .flex-between": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
  },
});

const DrawerSetting = ({ open, onClose, data, handleOpenAlert }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { result : UserProfile } = useSelector(state => state.userProfile);
  const [listHolidayPatterns, setListHolidayPatterns] = useState([]);

  const { clearErrors, control, handleSubmit, setError, setValue, watch, reset, formState: { errors } } =
    useForm({
      defaultValues: {
        year: new Date(),
        isMainHoliday: true,
        idHolidayPattern: "",
      },
    });

  useEffect(() => {
    reset({
        year: new Date(),
        isMainHoliday: true,
        idHolidayPattern: "",
    });
  }, [open]);

  useEffect(() => {
    if (watch("year") && watch("year") !== "") {
        fetchSetValue();
    }
  }, [data, watch("year")]);

  const fetchSetValue = () => {
    let isMainHoliday = true;
    let idHolidayPattern = "";
    let listHolidayPatternsYear = [];

    if(data && data.holidayPattern && data.holidayPattern[dayjs(watch("year")).format("YYYY")]){
        let holidayPatternYear = data.holidayPattern[dayjs(watch("year")).format("YYYY")];

        idHolidayPattern = holidayPatternYear.idHolidayPattern;
        listHolidayPatternsYear = holidayPatternYear.listHolidayPattern.filter((v) => (v.isMainPattern !== 1));

        if(idHolidayPattern){
            let findHP = holidayPatternYear.listHolidayPattern.find((v) => v.idHolidayPattern === idHolidayPattern);
            
            if(findHP){
                isMainHoliday = findHP.isMainPattern === 1 ? true : false;
            }
        }
    }

    setListHolidayPatterns(listHolidayPatternsYear);
    setValue("isMainHoliday", isMainHoliday);
    setValue("idHolidayPattern", idHolidayPattern);
  };

  const handleSubmitForm = async (value) => {
    if(!value.isMainHoliday && value.idHolidayPattern === "") {
        setError("idHolidayPattern", {
            type: "required",
            message: t(`ThisFieldIsRequired`)
        });
        return;
    }

    let idHolidayPattern = value.idHolidayPattern;

    if((idHolidayPattern === "") && value.isMainHoliday){
        if(data.holidayPattern[dayjs(value.year).format("YYYY")]){
            let listHP = data.holidayPattern[dayjs(value.year).format("YYYY")].listHolidayPattern;
            let findHP = listHP.find((hp) => (hp.isMainPattern === 1));
            if(findHP){
                idHolidayPattern = findHP.idHolidayPattern;
            }else{
                idHolidayPattern = null;
            }
        }else{
            idHolidayPattern = null;
        }
    }

    let formData = {
        year: value.year,
        idShiftGroup: data.idShiftGroup,
        idHolidayPattern: idHolidayPattern
    }

    const result = await dispatch(updateShiftGroupHolidayPattern(formData));
    if(result.status === 200){
      handleOpenAlert({
        alertType: "success"
      });
      dispatch(getShift(UserProfile.idCompany));
      onClose();
    }else{
      handleOpenAlert({
        alertType: "error"
      });
    }
  };

  const disableHolidayPattern = () => {
    return parseInt(dayjs(watch("year")).year()) < parseInt(dayjs().year());
  };

  return (
    <DrawerCustom
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography>{t("Setting")}</Typography>
          <Typography>
            {`ชื่อกะ :`}
            <span
              style={{ fontSize: "20px", fontWeight: "500", marginLeft: "8px" }}
            >
              {(data && data.nameShiftGroup) || "-"}
            </span>
          </Typography>
        </div>
      }
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Content>
        {data ? (
          <div className="content-padding">
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <Typography fontWeight="600" fontSize="14px" color="#919eab">
                {t("HolidayPattern")}
              </Typography>
              <div className="flex-between">
                <Typography className="sub-topic">
                  {t("AnnualHoliday")}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name={"year"}
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          clearErrors();
                        }}
                        views={["year"]}
                        inputFormat="YYYY"
                        minDate={
                          data && data.shiftStartDate
                            ? dayjs(data.shiftStartDate).date(1).month(0)
                            : dayjs().date(1).month(0)
                        }
                        maxDate={dayjs().date(1).month(0).add(1, "year")}
                        renderInput={(params) => {
                          return (
                            <TextFieldTheme
                              {...params}
                              style={{
                                width: "fit-content",
                                minWidth: "200px",
                                maxWidth: "200px",
                              }}
                            />
                          );
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>
              <div className="flex-between">
                <Typography className="sub-topic">
                  {t("useMainHoliday")}
                </Typography>
                <Controller
                  name={"isMainHoliday"}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        marginRight: "0",
                      }}
                      control={
                        <Checkbox
                          style={{
                            right: "-9px",
                          }}
                          {...field}
                          checked={field.value}
                          onChange={(e) => {
                            setValue("isMainHoliday", e.target.checked, {
                              shouldDirty: true,
                            });
                            setValue("idHolidayPattern", "");
                            if(errors.idHolidayPattern){
                                clearErrors("idHolidayPattern");
                            }
                          }}
                          disabled={disableHolidayPattern()}
                        />
                      }
                    />
                  )}
                />
              </div>
              {!watch("isMainHoliday") 
              &&
              listHolidayPatterns &&
              listHolidayPatterns.length <= 0 
              ? (
                <Typography
                  style={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.26)",
                    fontWeight: "700",
                  }}
                >
                  * {t("addOtherHolidayType")}
                </Typography>
              ) : (
                <Fragment></Fragment>
              )}
              {!watch("isMainHoliday") 
              &&
              listHolidayPatterns &&
              listHolidayPatterns.length >= 0 
              ? (
                <Box>
                  <Controller
                    name={"idHolidayPattern"}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldTheme
                        {...field}
                        style={{
                          marginBottom: "16px",
                        }}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          if(fieldState.error){
                            clearErrors("idHolidayPattern");
                          }
                        }}
                        helperText={
                          fieldState.error ? fieldState.error.message : null
                        }
                        error={fieldState.error ? true : false}
                        select
                        SelectProps={{
                          displayEmpty: true,
                        }}
                        disabled={
                          disableHolidayPattern() 
                          || listHolidayPatterns.length <= 0
                        }
                      >
                        {listHolidayPatterns.length > 0 ? (
                          listHolidayPatterns.map((pattern) => {
                            return (
                              <MenuItem
                                key={`${pattern.idHolidayPattern}_${pattern.name}`}
                                value={pattern.idHolidayPattern}
                              >
                                {pattern.name}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value="" disabled>
                            ไม่พบวันหยุด
                          </MenuItem>
                        )}
                      </TextFieldTheme>
                    )}
                  />
                </Box>
              ) : (
                <Fragment></Fragment>
              )}
              <div className="flex-between">
                <ButtonBlue onClick={onClose}>{t("Cancel")}</ButtonBlue>
                <ButtonBlue variant="contained" type="submit">
                  {t("Confirm")}
                </ButtonBlue>
              </div>
            </form>
          </div>
        ) : (
          <Fragment></Fragment>
        )}
      </Content>
    </DrawerCustom>
  );
};

export default DrawerSetting;
